import { useEffect, useRef, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { AddLinkRounded, AutoAwesomeRounded, CheckCircleRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import AndieGenericToolRender from './AndieGenericToolRender';
import { ActionRenderAi } from '@/ai/types/action-render-ai';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import ConnectNetDialog from '@/components/social-profile/ConnectNetDialog';
import { BrandsDocument } from '@/graphql/generated/brands.generated';
import { useAndieChatControls } from '@/providers/andie-chat-provider';
import { useSession } from '@/providers/session-provider';
import { isBrandPricingWorkspace } from '@/utils/users-path-filter';

type ActionResponse = { prompted: boolean };

export type AndieConnectFirstProfileProps = ActionRenderAi<ActionResponse>;

const AndieConnectFirstProfile = (props: AndieConnectFirstProfileProps) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const { stop, sendMessage } = useAndieChatControls();
    const { workspace } = useSession();
    const isBrand = isBrandPricingWorkspace(workspace);

    const { data: brandsData, loading: loadingBrands } = useQuery(BrandsDocument, {
        variables: { take: 1, skip: 0, includeEmpty: true },
        skip: !isBrand,
    });

    const brandPublicId: number | undefined = isBrand
        ? (brandsData?.groups?.items?.[0]?.publicId ?? undefined)
        : undefined;
    const executingSteps = useRef(0);
    const stoppedRef = useRef(false);
    const continueSentRef = useRef(false);

    // Once the user connects a profile, automatically tell Andie to resume the task that was blocked.
    const handleConnected = () => {
        setOpen(false);
        if (continueSentRef.current) return;
        continueSentRef.current = true;
        sendMessage(t('andie.tool.connect-first-profile.continue-message'));
    };

    // Connecting a profile blocks the whole task, so the moment this tool runs in a live turn we stop
    // the run once - the harness would otherwise loop with the evaluator for a final response. We wait
    // for the user to connect and come back instead.
    useEffect(() => {
        if (props.status === ActionResponseStatusAi.EXECUTING) {
            executingSteps.current += 1;
        }
        if (
            props.status === ActionResponseStatusAi.COMPLETE &&
            executingSteps.current > 0 &&
            !stoppedRef.current
        ) {
            stoppedRef.current = true;
            stop();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.status]);

    const isComplete = props.status === ActionResponseStatusAi.COMPLETE;

    // While the (instant) tool call is still settling, keep the lightweight inline render.
    if (!isComplete) {
        return (
            <AndieGenericToolRender<ActionResponse>
                title={() => t('andie.tool.connect-first-profile.title')}
                icon={<AddLinkRounded fontSize="small" />}
                loadingMessages={() => [t('andie.tool.connect-first-profile.loading')]}
                completeMessage={() => t('andie.tool.connect-first-profile.complete')}
                {...props}
            />
        );
    }

    const benefits = [
        t('andie.tool.connect-first-profile.benefit-1'),
        t('andie.tool.connect-first-profile.benefit-2'),
        t('andie.tool.connect-first-profile.benefit-3'),
    ];

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'andie.main',
                    background: (theme) =>
                        `linear-gradient(135deg, ${theme.palette.andie.main}14, transparent 70%)`,
                }}
            >
                <Stack gap={1.5}>
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                bgcolor: 'andie.main',
                                color: 'common.white',
                                flexShrink: 0,
                            }}
                        >
                            <AutoAwesomeRounded sx={{ fontSize: 18 }} />
                        </Box>
                        <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                            {t('andie.tool.connect-first-profile.card-title')}
                        </Typography>
                    </Stack>

                    <Stack gap={0.75}>
                        {benefits.map((benefit) => (
                            <Stack key={benefit} direction="row" alignItems="flex-start" gap={1}>
                                <CheckCircleRounded
                                    sx={{ fontSize: 16, color: 'andie.main', mt: '2px', flexShrink: 0 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {benefit}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>

                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddLinkRounded />}
                        onClick={() => setOpen(true)}
                        // Without the brand yet, the dialog would send the user to the brands page.
                        disabled={loadingBrands}
                        sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                    >
                        {t('andie.tool.connect-first-profile.button')}
                    </Button>
                </Stack>
            </Paper>

            <ConnectNetDialog
                open={open}
                onlyPublish
                brandId={brandPublicId}
                onClose={() => setOpen(false)}
                onConnect={handleConnected}
            />
        </>
    );
};

export default AndieConnectFirstProfile;
